import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Clock, BookOpen, FileText, BarChart3, Lightbulb, X } from "lucide-react";
import { SectionHeading } from "../components/shared/SectionHeading";
import CTABanner from "../components/shared/CTABanner";
import { RESOURCES } from "../lib/data";
import { cn } from "../lib/utils";

const TYPE_ICONS = {
    Blog: BookOpen,
    Whitepaper: FileText,
    Guide: Lightbulb,
    Report: BarChart3,
    "Case Report": FileText,
};

const TYPE_FILTERS = ["All", "Blog", "Whitepaper", "Guide", "Report", "Case Report"];

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function Resources() {
    const [filter, setFilter] = useState("All");
    const [activeResource, setActiveResource] = useState(null);

    // Body scroll lock + ESC to close
    useEffect(() => {
        if (!activeResource) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape") setActiveResource(null);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKey);
        };
    }, [activeResource]);

    const filtered = useMemo(() => {
        const list = filter === "All" ? RESOURCES : RESOURCES.filter((r) => r.type === filter);
        return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
    }, [filter]);

    const counts = useMemo(() => {
        const c = { All: RESOURCES.length };
        for (const r of RESOURCES) c[r.type] = (c[r.type] || 0) + 1;
        return c;
    }, []);

    return (
        <main data-testid="page-resources" className="relative pt-32 md:pt-40">
            <section className="relative pb-12">
                <div className="absolute inset-0 -z-10 grid-lines opacity-40" />
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Resources"
                        title="Insights from our practice leaders."
                        subtitle="Blogs, whitepapers, guides and industry reports authored by the practitioners inside Netfotech."
                    />
                </div>
            </section>

            {/* FILTERS */}
            <section
                data-testid="resources-filters"
                className="relative py-4 sticky top-[72px] z-30 bg-ink-950/85 backdrop-blur-xl border-y border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <div className="flex flex-wrap gap-2">
                        {TYPE_FILTERS.map((t) => {
                            const isActive = t === filter;
                            const c = counts[t] || 0;
                            return (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => setFilter(t)}
                                    data-testid={`resource-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={cn(
                                        "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[12.5px] font-medium transition-all",
                                        isActive
                                            ? "bg-brand-linear text-white border-transparent shadow-glow-brand"
                                            : "glass border-white/10 text-zinc-300 hover:border-white/30"
                                    )}
                                >
                                    {t}
                                    <span
                                        className={cn(
                                            "rounded-full px-1.5 py-0.5 text-[10px] font-mono",
                                            isActive ? "bg-white/20" : "bg-white/5 text-zinc-400"
                                        )}
                                    >
                                        {c}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* GRID */}
            <section className="relative py-14">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    <AnimatePresence mode="popLayout">
                        {filtered.map((r, i) => {
                            const Icon = TYPE_ICONS[r.type] || FileText;
                            return (
                                <motion.article
                                    key={r.id}
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.4, delay: (i % 6) * 0.04 }}
                                    className="group relative overflow-hidden glass rounded-3xl border border-white/5 p-7 md:p-8 brand-border-hover tracing-beam flex flex-col"
                                    data-testid={`resource-${r.id}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-[10.5px] uppercase tracking-[0.22em] text-brand-green">
                                            <Icon className="h-3 w-3" />
                                            {r.type}
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[11.5px] text-zinc-500">
                                            <Clock className="h-3.5 w-3.5" />
                                            {r.readTime}
                                        </div>
                                    </div>
                                    {r.category && (
                                        <div className="mt-4 font-mono text-[11px] text-zinc-500">
                                            {r.category}
                                        </div>
                                    )}
                                    <h3 className="mt-2 font-display text-[20px] md:text-[22px] font-semibold tracking-tight leading-snug">
                                        {r.title}
                                    </h3>
                                    <p className="mt-3 text-[14px] text-zinc-400 leading-relaxed flex-1">
                                        {r.summary}
                                    </p>
                                    <div className="mt-7 flex items-end justify-between pt-5 border-t border-white/5">
                                        <div className="text-[11.5px] text-zinc-500">
                                            <div>{r.author}</div>
                                            <div className="text-zinc-600">{formatDate(r.date)}</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setActiveResource(r)}
                                            data-testid={`resource-read-${r.id}`}
                                            className="inline-flex items-center gap-1.5 text-brand-green hover:text-white transition-colors text-[13px] font-medium"
                                        >
                                            Read
                                            <ArrowUpRight className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </AnimatePresence>
                </div>

                {filtered.length === 0 && (
                    <div
                        data-testid="resources-empty"
                        className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 text-center text-zinc-500 py-16"
                    >
                        No resources in this category yet. Try another filter.
                    </div>
                )}
            </section>

            {/* READ MODAL */}
            <AnimatePresence>
                {activeResource && (
                    <motion.div
                        data-testid="resource-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-ink-950/80 backdrop-blur-md"
                        onClick={() => setActiveResource(null)}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 30, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 20, scale: 0.96 }}
                            transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-2xl glass-strong rounded-3xl border border-white/10 overflow-hidden max-h-[85vh] flex flex-col"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="resource-modal-title"
                        >
                            <button
                                type="button"
                                onClick={() => setActiveResource(null)}
                                data-testid="resource-modal-close"
                                aria-label="Close"
                                className="absolute top-5 right-5 inline-flex h-9 w-9 items-center justify-center rounded-full glass border border-white/10 text-zinc-300 hover:text-white transition z-10"
                            >
                                <X className="h-4 w-4" />
                            </button>

                            <div className="p-8 md:p-10 overflow-y-auto">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-[10.5px] uppercase tracking-[0.22em] text-brand-green">
                                    {activeResource.type}
                                </div>
                                {activeResource.category && (
                                    <div className="mt-4 font-mono text-[11px] text-zinc-500">
                                        {activeResource.category}
                                    </div>
                                )}
                                <h2
                                    id="resource-modal-title"
                                    className="mt-3 font-display text-2xl md:text-3xl font-bold tracking-tighter leading-tight"
                                >
                                    {activeResource.title}
                                </h2>
                                <div className="mt-3 flex items-center gap-4 text-[12px] text-zinc-500">
                                    <span>{activeResource.author}</span>
                                    <span>·</span>
                                    <span>{formatDate(activeResource.date)}</span>
                                    <span>·</span>
                                    <span className="inline-flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {activeResource.readTime}
                                    </span>
                                </div>

                                <p className="mt-6 text-zinc-200 text-[15px] leading-relaxed">
                                    {activeResource.summary}
                                </p>
                                {activeResource.content && (
                                    <p className="mt-4 text-zinc-400 text-[14.5px] leading-relaxed whitespace-pre-wrap">
                                        {activeResource.content}
                                    </p>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <section className="relative py-16 md:py-20">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <CTABanner
                        eyebrow="Subscribe for monthly insights"
                        title="Stay ahead with the Netfotech briefing."
                        subtitle="One curated email each month patterns, reference architectures and leadership notes."
                    />
                </div>
            </section>
        </main>
    );
}
